import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip
} from "@mui/material";
import {
  Chat as ChatIcon,
  Groups as GroupsIcon,
  AttachFile as AttachIcon,
  Security as SecurityIcon
} from "@mui/icons-material";

const InAppMessaging = () => {
  const theme = useTheme();

  const messagingFeatures = [
    {
      icon: <ChatIcon sx={{ fontSize: 40 }} />,
      title: "Real-time Chat",
      description: "Instant messaging with cleaners and support team"
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      title: "Group Conversations",
      description: "Coordinate with multiple team members efficiently"
    },
    {
      icon: <AttachIcon sx={{ fontSize: 40 }} />,
      title: "File Sharing",
      description: "Share photos, documents, and checklists easily"
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: "Secure & Private",
      description: "End-to-end encrypted conversations for your privacy"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="💬 In-App Messaging" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          In-App Messaging
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Streamline communication with your cleaning team and support staff through 
          our secure, real-time messaging platform built directly into the app.
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: 8 }}>
        {messagingFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="600">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={6} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Stay Connected
          </Typography>
          <Typography variant="body1" color={theme.palette.text.secondary} sx={{ mb: 3, lineHeight: 1.7 }}>
            Our in-app messaging system ensures you're always connected with your cleaning team. 
            Whether it's last-minute instructions, special requests, or emergency situations, 
            communicate instantly without leaving the app.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {["Instant notifications", "Read receipts", "Message history", "Multi-language support"].map((item, idx) => (
              <Chip
                key={idx}
                label={item}
                variant="outlined"
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main
                }}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              height: "100%"
            }}
          >
            <ChatIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="600">
              Real-time Communication
            </Typography>
            <Typography variant="body1" color={theme.palette.text.secondary}>
              Get instant responses and keep everyone on the same page with our powerful messaging platform.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default InAppMessaging;